import { ACCEL_CAP_G } from './accel'
import type { LapTrace } from './laps'

/**
 * Per-tyre traction from the GPS accelerations.
 *
 * Bike and rider are one rigid mass. Longitudinal acceleration moves load between the wheels
 * along the wheelbase; drive is all on the rear; braking is on the front except for engine
 * braking, which the rear carries up to a threshold and never beyond what its load can
 * transmit. Lateral force is shared in proportion to each tyre's load, so both tyres see the
 * same lateral demand. Each tyre's horizontal force divided by its own vertical load is its
 * demand, in g: how hard that tyre is working, whatever the load on it.
 *
 * Not modelled: aerodynamic drag, chain pull and anti-squat, suspension geometry, tyre camber
 * forces, the rider moving about. The result is an estimate from GPS, not a measurement.
 */
export interface BikeParams {
  /** Wet, without rider (kg) */
  bikeMass: number
  /** Height of the bike's centre of gravity above the ground (m) */
  bikeCogHeight: number
  /** Share of the bike's weight on the front wheel at rest, 0..1 */
  bikeFrontFraction: number
  /** Rider with gear (kg) */
  riderMass: number
  /** Height of the seated rider's centre of gravity above the ground (m) */
  riderCogHeight: number
  /** Share of the rider's weight on the front wheel at rest, 0..1 */
  riderFrontFraction: number
  /** Axle to axle (m) */
  wheelbase: number
  /** Deceleration the rear carries as engine braking before the front brake takes the rest (g) */
  engineBrakeG: number
}

export const DEFAULT_BIKE: BikeParams = {
  bikeMass: 200,
  bikeCogHeight: 0.5,
  bikeFrontFraction: 0.5,
  riderMass: 75,
  riderCogHeight: 1.0,
  riderFrontFraction: 0.45,
  wheelbase: 1.4,
  engineBrakeG: 0.15,
}

/** A wheel is treated as lifted when less than this share of the weight is left on it. */
export const LOAD_FLOOR = 0.02
export interface TyrePoint {
  /** Lateral demand (g), + right turn */
  x: number
  /** Longitudinal demand (g), + drive, − braking */
  y: number
  /** Share of the total weight on this tyre */
  load: number
}

export interface TractionSample {
  front: TyrePoint
  rear: TyrePoint
  /** The wheel the load transfer took to the floor, if any */
  lifted: 'front' | 'rear' | null
}

/** Combined centre of gravity: height and static front share. */
export function combinedCog(p: BikeParams): { height: number; frontFraction: number } {
  const m = p.bikeMass + p.riderMass
  if (m <= 0) return { height: p.bikeCogHeight, frontFraction: p.bikeFrontFraction }
  return {
    height: (p.bikeMass * p.bikeCogHeight + p.riderMass * p.riderCogHeight) / m,
    frontFraction: (p.bikeMass * p.bikeFrontFraction + p.riderMass * p.riderFrontFraction) / m,
  }
}

/** Front and rear tyre demand for one longitudinal (`ax`, + accelerating) and lateral (`ay`) reading in g. */
export function tyreForces(ax: number, ay: number, p: BikeParams): TractionSample {
  const { height, frontFraction } = combinedCog(p)
  const transfer = p.wheelbase > 0 ? (ax * height) / p.wheelbase : 0
  let nFront = frontFraction - transfer
  let lifted: TractionSample['lifted'] = null
  if (nFront < LOAD_FLOOR) {
    nFront = LOAD_FLOOR
    lifted = 'front'
  } else if (nFront > 1 - LOAD_FLOOR) {
    nFront = 1 - LOAD_FLOOR
    lifted = 'rear'
  }
  const nRear = 1 - nFront

  let fxFront = 0
  let fxRear = 0
  if (ax >= 0) fxRear = ax
  else {
    // Engine braking on the rear, capped by what its load can transmit; the front brake does the rest.
    const rear = Math.min(-ax, Math.max(0, p.engineBrakeG), nRear)
    fxRear = rear > 0 ? -rear : 0
    const frontBrake = -ax - rear
    fxFront = frontBrake > 0 ? -frontBrake : 0
  }
  return {
    front: { x: ay, y: fxFront / nFront, load: nFront },
    rear: { x: ay, y: fxRear / nRear, load: nRear },
    lifted,
  }
}

/** One lap's samples for both tyres, aligned to the trace's distance axis. */
export interface TraceTraction {
  frontX: Float64Array
  frontY: Float64Array
  rearX: Float64Array
  rearY: Float64Array
  /** 1 where the sample is riding data, 0 where it is noise or standing still */
  valid: Uint8Array
}

const LON = 'GPS Longitudinal Acc'
const LAT = 'GPS Lateral Acc'

/** Standing still is already zero in both channels (the lateral one is forced to 0 when crawling). */
export function tractionForTrace(t: LapTrace, p: BikeParams): TraceTraction | null {
  const lon = t.channels[LON]
  const lat = t.channels[LAT]
  const n = t.distance.length
  if (!lon || !lat || lon.length !== n || lat.length !== n) return null
  const out: TraceTraction = {
    frontX: new Float64Array(n),
    frontY: new Float64Array(n),
    rearX: new Float64Array(n),
    rearY: new Float64Array(n),
    valid: new Uint8Array(n),
  }
  for (let i = 0; i < n; i++) {
    const ax = lon[i]
    const ay = lat[i]
    if (!Number.isFinite(ax) || !Number.isFinite(ay)) continue
    if (Math.hypot(ax, ay) > ACCEL_CAP_G) continue
    const s = tyreForces(ax, ay, p)
    out.frontX[i] = s.front.x
    out.frontY[i] = s.front.y
    out.rearX[i] = s.rear.x
    out.rearY[i] = s.rear.y
    out.valid[i] = 1
  }
  return out
}

/** The largest demand among the valid samples of a lap: the ring's radius. 0 when none. */
export function maxDemand(xs: Float64Array, ys: Float64Array, valid: Uint8Array): number {
  let best = 0
  for (let i = 0; i < xs.length; i++) {
    if (!valid[i]) continue
    const d = Math.hypot(xs[i], ys[i])
    if (d > best) best = d
  }
  return best
}

/** Per-tyre peaks of a lap, for the report. */
export interface TractionPeaks {
  frontMax: number
  rearMax: number
  /** Strongest front braking demand (g, positive) */
  frontBrake: number
  /** Strongest rear drive demand (g) */
  rearDrive: number
  /** Strongest lateral demand (g, either side) */
  lateral: number
}

export function tractionPeaks(tr: TraceTraction): TractionPeaks {
  const out: TractionPeaks = { frontMax: 0, rearMax: 0, frontBrake: 0, rearDrive: 0, lateral: 0 }
  for (let i = 0; i < tr.valid.length; i++) {
    if (!tr.valid[i]) continue
    out.frontMax = Math.max(out.frontMax, Math.hypot(tr.frontX[i], tr.frontY[i]))
    out.rearMax = Math.max(out.rearMax, Math.hypot(tr.rearX[i], tr.rearY[i]))
    out.frontBrake = Math.max(out.frontBrake, -tr.frontY[i])
    out.rearDrive = Math.max(out.rearDrive, tr.rearY[i])
    out.lateral = Math.max(out.lateral, Math.abs(tr.frontX[i]))
  }
  return out
}
